import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import { Download, ExternalLink } from '@lifeomic/chromicons';
import ExternalLinkButton from './ExternalLinkButton';
import { useActiveAccountId, useActiveProjectId } from '../../hooks';
import { useIntl, defineMessages } from 'react-intl';
import FileService from '../../services/FileService';
import useService from '../../hooks/useService';
import { useNotify } from '../../hooks/useNotify';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { hasCsvExtension, isCsvMime } from '../../util/knowledgeBaseCitation';
import { CsvAttachmentModal } from '../SubjectViewer/Modules/AITools/Attachments/CsvAttachmentViewer';

const fileService = new FileService();

const dicomContentType = 'application/dicom';

const isViewableContentType = (contentType: string) =>
  [
    dicomContentType,
    'audio/wave',
    'audio/wav',
    'audio/x-wav',
    'audio/x-pn-wav',
    'audio/mpeg',
    'audio/ogg',
    'video/ogg',
    'application/ogg',
    'video/webm',
    'audio/webm',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'text/plain',
    'application/xml',
    'application/octet-stream',
    'text/html',
    'application/json',
  ].includes(contentType);

export const useStyles = makeStyles((_theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

const messages = defineMessages({
  download: {
    id: 'fileManagementControls.download',
    defaultMessage: 'Download',
  },
  open: {
    id: 'fileManagementControls.open',
    defaultMessage: 'Open',
  },
  openError: {
    id: 'fileManagementControls.openError',
    defaultMessage: 'Error opening file',
  },
  downloadError: {
    id: 'fileManagementControls.downloadError',
    defaultMessage: 'Error downloading file',
  },
});

export type FileManagementControlsClasses = GetClasses<typeof useStyles>;

export interface FileManagementControlsOwnProps {
  enableOpen?: boolean;
  enableRequestDownloadLink?: boolean;
  fileId: string;
  fileName?: string;
  contentType?: string;
  tooltipText?: string;
  className?: string;
}

export type FileManagementControlsProps = FileManagementControlsOwnProps;

/**
  @description View note below - you may not need this!

   _   _ _____ _     _   _   _______
  | | | |  _  | |   ( ) | | | | ___ \
  | |_| | | | | |   |/  | | | | |_/ /
  |  _  | | | | |       | | | |  __/
  | | | \ \_/ / |____   | |_| | |
  \_| |_/\___/\_____/    \___/\_|

  You *may* not want to use this component!  If you are embedding these controls
  *outside* of a table, then this is fine; however, if you are rendering
  these *inside* of a table, you'll want to use the following new components:

  - FileDeleteAction
  - FileDownloadAction
  - FileRenameAction

 */
const FileManagementControls = (props: FileManagementControlsProps) => {
  const { formatMessage } = useIntl();
  const classes = useStyles(props);
  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [notify] = useNotify();
  const [fetchOpenLink] = useService(fileService.fetchOpenLink);
  const [fetchDownloadLink] = useService(fileService.fetchDownloadLink);

  const isCsvFile =
    isCsvMime(props.contentType) || hasCsvExtension(props.fileName);

  const [csvModal, setCsvModal] = React.useState<{
    isOpen: boolean;
    url?: string;
    downloadUrl?: string;
    fileName: string;
  }>({ isOpen: false, fileName: props.fileName ?? 'file.csv' });

  const handleOpenPdf = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchOpenLink(props.fileId);
      const downloadUrl = result?.data?.downloadUrl;
      if (!downloadUrl) throw new Error('No open url for file');
      window.open(downloadUrl, '_blank');
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.openError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [fetchOpenLink, formatMessage, notify, props.fileId]);

  // CSV files are unhelpful to "open" via window.open (the browser just
  // re-downloads them), so render them inline with the in-app CSV table viewer
  // instead. We need both the open URL (used to fetch the body) and the
  // content-disposition download URL (wired up to the modal's "Download"
  // button) so the user can still grab the raw file when needed.
  const handleOpenCsv = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const openResult = await fetchOpenLink(props.fileId);
      const openUrl = openResult?.data?.downloadUrl;
      const resolvedName =
        openResult?.data?.name ?? props.fileName ?? 'file.csv';
      if (!openUrl) throw new Error('No open url for file');
      const dlResult = await fetchDownloadLink(props.fileId);
      const downloadUrl = dlResult?.data?.downloadUrl ?? openUrl;
      setCsvModal({
        isOpen: true,
        url: openUrl,
        downloadUrl,
        fileName: resolvedName,
      });
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.openError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchDownloadLink,
    fetchOpenLink,
    formatMessage,
    notify,
    props.fileId,
    props.fileName,
  ]);

  const closeCsvModal = React.useCallback(() => {
    setCsvModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleDownloadFile = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchDownloadLink(props.fileId);
      const downloadUrl = result?.data?.downloadUrl;
      if (!downloadUrl) throw new Error('No download url for file');
      window.open(downloadUrl, '_blank');
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.downloadError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [fetchDownloadLink, formatMessage, notify, props.fileId]);

  return (
    <div className={clsx(classes.root, props.className)}>
      {isCsvFile && props.enableOpen && (
        <IconButton
          aria-label={formatMessage(messages.open)}
          icon={ExternalLink}
          onClick={handleOpenCsv}
          size={0}
          disabled={isLoading}
        />
      )}
      {!isCsvFile &&
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        isViewableContentType(props.contentType) &&
        props.enableOpen && (
          <ExternalLinkButton
            fileId={props.fileId}
            accountId={accountId}
            projectId={projectId}
            tooltipText={props.tooltipText || formatMessage(messages.open)}
          />
        )}
      {!isCsvFile &&
        props.contentType === 'application/pdf' &&
        props.enableOpen && (
          <IconButton
            aria-label={formatMessage(messages.open)}
            icon={ExternalLink}
            onClick={handleOpenPdf}
            size={0}
            disabled={isLoading}
          />
        )}
      {props.enableRequestDownloadLink && (
        <IconButton
          aria-label={formatMessage(messages.download)}
          icon={Download}
          size={0}
          onClick={handleDownloadFile}
          disabled={isLoading}
        />
      )}
      {csvModal.isOpen && (
        <CsvAttachmentModal
          isOpen={csvModal.isOpen}
          onDismiss={closeCsvModal}
          url={csvModal.url}
          downloadUrl={csvModal.downloadUrl}
          fileName={csvModal.fileName}
          data-testid="file-management-controls-csv"
        />
      )}
    </div>
  );
};

export default React.memo(FileManagementControls);
